import { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useMutation } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import { handleValidationsErrorsAPI, history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import useQueryParams from '~/hooks/useQuery'
import {
  createCampaignKey,
  createCampaignService,
  createObjectiveBasedCampaignService
} from '~/modules/retailMedia/services/campaign'

import { applyPreparedCampaignBody } from '../helpers/applyPreparedCampaignBody'
import { DTOCreateObjectiveBasedCampaign } from '../strategies/ObjectiveBased/dto'
import { useCampaignForm } from './useCampaignForm'
import { usePrepareCampaignCreateBody } from './usePrepareCampaignCreateBody'

export const useCreateCampaign = ({
  campaignStrategy,
  formRef
}: UseCreateCampaignProps) => {
  const { t } = useTranslation()
  const { toast } = useContext(FeedbackContext)
  const { lengthCentsCurrency } = useAppInfo()
  const rmid = useQueryParams().get('rmid') as PublisherId

  const { handleDebugSubmit } = useCampaignForm()
  const { prepareCampaignCreateBody } = usePrepareCampaignCreateBody()

  const [CreateAlertErrror, setErrorCampaign] = useErrorApiAlert({
    title: t('rm:campaign.create.toast.error.message')
  })

  const { mutate, isPending } = useMutation({
    mutationKey: [createCampaignKey],
    mutationFn:
      campaignStrategy === 'objective_based'
        ? createObjectiveBasedCampaignService
        : createCampaignService,
    onSuccess: response => {
      history.push(`/retail-media/campaign/view/${response.data.id}`)

      toast.success(t('rm:campaign.create.toast.success.message'))
    },
    onError: error => {
      setErrorCampaign(error)

      handleValidationsErrorsAPI(error, formRef)

      toast.critical(t('rm:campaign.create.toast.error.message'))
    }
  })

  const handleSubmit = useCallback(
    async (body: CreateCampaignObjectiveBasedFormData) => {
      const preData: DTOCreateObjectiveBasedCampaignPayload = {
        ...body,
        publisherId: rmid,
        lengthCentsCurrency
      }

      const payload = DTOCreateObjectiveBasedCampaign(preData)
      const action = handleDebugSubmit || mutate

      applyPreparedCampaignBody(payload, prepareCampaignCreateBody, action)
    },
    [
      rmid,
      lengthCentsCurrency,
      handleDebugSubmit,
      mutate,
      prepareCampaignCreateBody
    ]
  )

  return { loading: isPending, handleSubmit, CreateAlertErrror }
}
